
import Page from '@root/common/base/Page';
import Meta from '@root/common/utils/Meta';
import { GetCustomerData } from '@root/modules/customer/types/customer';
import CustomerDataService from '@root/modules/customer/services/CustomerData.service';
import getPageAdsSettings from '@root/modules/customer/utils/getPageAdsSettings';
import Header from '@root/modules/customer/components/myDelfi/Header.vue';
import FirstPage from '@root/modules/customer/components/myDelfi/FirstPage.vue';
import CategoriesSelect from '@root/modules/customer/components/myDelfi/CategoriesSelect.vue';
import CategoriesNavigation from '@root/modules/customer/components/myDelfi/CategoriesNavigation.vue';
import Headlines from '@root/modules/customer/components/myDelfi/Headlines.vue';
import BookmarksPageSkeletonLoader from '@root/modules/customer/components/BookmarksPageSkeletonLoader.vue';

interface cXWindow extends Window {
  [index: string]: any;
}

declare let window: cXWindow;

export default Page.extend({
  components: {
    Header,
    FirstPage,
    CategoriesSelect,
    CategoriesNavigation,
    Headlines,
    BookmarksPageSkeletonLoader,
  },
  data() {
    return {
      usersCategories: null as GetCustomerData['data']['data'] | null,
      customerDataService: null as CustomerDataService | null,
      readingHistoryHeadlinesIds: [] as number[],
      bookmarksHeadlinesIds: [] as number[],
      pager: {
        limit: 50,
        offset: 0,
        count: 0,
      },
      activePage: 'Loading',
    };
  },
  computed: {
    customerToken(): string {
      return this.$store.state.piano.token;
    },
    customerHasAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    isPianoScriptInited() {
      return this.$store.state.piano.isScriptInited;
    },
  },
  methods: {
    async getPageData() {
      const limit = this.pager.limit;
      const offset = 0;

      await this.getUsersCategories(limit, offset);
      await this.getBookmarks();
      await this.getReadingHistory();
    },
    async getUsersCategories(limit?: number, offset?: number) {
      const response =
        (await this.customerDataService?.get({
          key: ['mydelfi-selected-categories'],
          limit: limit ?? this.pager.limit,
          offset: offset ?? this.pager.offset,
        })) || null;

      if (response?.data.items.length) {
        response.data.items = response.data.items.filter((item) => String(item.value));
        this.usersCategories = response.data;
      } else {
        this.usersCategories = null;
        if (!window?.location?.hash?.includes('#category-select')) {
          this.setActivePage('FirstPage');
        } else {
          this.setActivePage('CategoriesSelect');
        }
      }
    },
    async updateUsersCategories(categories: string[]) {
      const selectedCategories = [] as string[];
      const unselectedCategories = [] as string[];

      const promises = categories.map(async (category: string) => {
        let response;
        if (this.usersCategories && this.usersCategories.items.find((item) => item.value === category)) {
          const item = this.usersCategories.items.find((item) => item.value === category);
          if (item) {
            try {
              response = await this.customerDataService?.delete({ id: item.id });
              unselectedCategories.push(category);
            } catch (e) {
              this.$sentry.captureException(e, {
                contexts: { vue: { componentName: 'MyDelfiButton', method: 'updateUsersCategories' } },
                tags: { 'process.server': process.server ? 'server' : 'client' },
              });
            }

            if (response?.data.status === true) {
              this.usersCategories.items = this.usersCategories.items.filter((item) => item.value !== category);
            }
          }
        } else {
          try {
            response = await this.customerDataService?.create({ key: 'mydelfi-selected-categories', value: category });
            selectedCategories.push(category);
          } catch (e) {
            this.$sentry.captureException(e, {
              contexts: { vue: { componentName: 'MyDelfiButton', method: 'updateUsersCategories' } },
              tags: { 'process.server': process.server ? 'server' : 'client' },
            });
          }

          if (response?.data?.customerData) {
            if (this.usersCategories) {
              this.usersCategories.items.push(response.data.customerData);
            }
          }
        }
      });

      await Promise.all(promises);
      this.logCategoriesSelect(selectedCategories, unselectedCategories);
      await this.getPageData();
      this.setActivePage('Headlines');
    },
    async getBookmarks() {
      await this.$store.dispatch('bookmarks/loadBookmarks', {
        customerToken: this.customerToken,
      });

      if (this.bookmarksHeadlinesIds.length === 0) {
        const bookmarks = this.$store.getters['bookmarks/getBookmarks'];
        if (bookmarks) {
          bookmarks.forEach((item: { value: any }) => {
            if (item.value) {
              this.bookmarksHeadlinesIds.push(Number(item.value));
            }
          });
        }
      }
    },
    async getReadingHistory() {
      if (this.readingHistoryHeadlinesIds.length === 0) {
        const response =
          (await this.customerDataService?.getReadingHistory({
            limit: 12,
            offset: 0,
          })) || null;
        if (response?.data.items) {
          const readingHistory = response?.data || null;

          readingHistory.items.forEach((item) => {
            if (item.articleId) {
              this.readingHistoryHeadlinesIds.push(item.articleId);
            }
          });
        }
      }
    },
    setActivePage(activePage: string) {
      this.activePage = activePage;
    },
    logCategoriesSelect(selectedCategories: string[], unselectedCategories: string[]) {
      if (window.cX && typeof window.cX.sendEvent === 'function') {
        window.cX.sendEvent(
          'mydelfi_categories_select',
          {
            user_id: this.$store.state.piano.profile.sub,
            selected_categories: JSON.stringify(selectedCategories),
            unselected_categories: JSON.stringify(unselectedCategories),
          },
          { origin: 'lth-delfi', persistedQueryId: '60134f11dac530e2cd5c308a42f829d82289a3b8' }
        );
      }
    },
  },
  watch: {
    isPianoScriptInited: {
      async handler() {
        if (this.customerToken && this.customerHasAccess) {
          this.customerDataService = new CustomerDataService();
          this.customerDataService?.setCustomerToken(this.customerToken);
          await this.getPageData();
        } else {
          this.setActivePage('FirstPage');
        }
      },
    },
    customerHasAccess: {
      async handler(access: boolean) {
        if (access === true && this.customerToken) {
          this.setActivePage('Loading');
          this.customerDataService = new CustomerDataService();
          this.customerDataService?.setCustomerToken(this.customerToken);
          await this.getPageData();
        } else {
          this.setActivePage('FirstPage');
        }
      },
    },
    usersCategories(value) {
      if (value && value.items && !window?.location?.hash?.includes('#category-select')) {
        this.setActivePage('Headlines');
      } else {
        this.setActivePage('CategoriesSelect');
      }
    },
  },
  async created() {
    this.setPageAdsSettings(getPageAdsSettings({ $channelConfig: this.$channelConfig }));

    if (this.customerHasAccess) {
      this.customerDataService = new CustomerDataService();
      this.customerDataService?.setCustomerToken(this.customerToken);
      await this.getPageData();
    }
  },
  mounted() {
    this.restoreScroll();
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();
    this.resetPageHeaderBottomNavigation();

    next();
  },
  head() {
    const meta = new Meta(this.$channelConfig('meta').base, this.$route.path);

    meta.title = meta.ogTitle = this.$t('myDelfi.meta.title') as string;
    meta.description = meta.ogDescription = this.$t('myDelfi.meta.description') as string;

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
